import React, { useState } from 'react';
import { generatePDF } from './generatePDF';

import {
  fontStyles,
  labelColors,
  valueColors,
  lineColors,
  SCHOOL_NAME_COLORS,
} from './utils/fontStyles';

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 16,
  padding: 18,
  boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
};

/* ================= MOVE CONTROLS ================= */

function MoveControls({
  onUp,
  onDown,
  onLeft,
  onRight,
}) {

  const btnStyle = {
    border: 'none',
    background: '#2563eb',
    color: '#fff',
    borderRadius: 12,
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: 700,
    minWidth: 70,
  };

  return (

    <div
      style={{
        display: 'grid',
        gap: 10,
        justifyContent: 'center',
      }}
    >

      <button
        style={btnStyle}
        onClick={onUp}
      >
        ↑ Up
      </button>

      <div
        style={{
          display: 'flex',
          gap: 10,
        }}
      >

        <button
          style={btnStyle}
          onClick={onLeft}
        >
          ← Left
        </button>

        <button
          style={btnStyle}
          onClick={onRight}
        >
          Right →
        </button>

      </div>

      <button
        style={btnStyle}
        onClick={onDown}
      >
        ↓ Down
      </button>

    </div>
  );
}

/* ================= MAIN COMPONENT ================= */

function SheetToPDF({ sheet1Data = [], sheet2Data = [] }) {

  const orientation = 'landscape';
  const mode = 'all';
  const classLabel = 'Class';

  const [gridCounts, setGridCounts] = useState({});
  const [totalGrids, setTotalGrids] = useState(0);

  const [headerMode, setHeaderMode] =
    useState('schoolName');

  const [imageMode, setImageMode] =
    useState('animation');

  // Dummy mode is a simplified output mode with only line-spacing controls.
  const [dummyMode, setDummyMode] = useState(false);
  const isDummyMode = dummyMode;

  /* ================= NEW: PRINTING METHOD ================= */

  const [printingMethod, setPrintingMethod] =
    useState('digital');

  // Images are selected in the browser and embedded directly into the PDF.
  const [uploadedBorderImages, setUploadedBorderImages] = useState([]);
  const [uploadedArtworkImages, setUploadedArtworkImages] = useState([]);

  const [labels, setLabels] = useState({

    schoolName: '',

    school: 'School',

    schoolAddressLine1: '',
    schoolAddressLine2: '',

    schoolAddressLine1FontSize: 9,
    schoolAddressLine2FontSize: 9,

    classStd: '1',

    sec: 'Sec',

    semester: 'Semester',

    name: 'Name',

    study: 'Study',

    rollNo: 'Roll No',

    schoolNameFontSize: 16.0,

    schoolNameFontStyle: 'PlayballRegular',

    // One user-selected font and size for every writing inside each PDF grid.
    gridFontStyle: fontStyles[0]?.font || 'Caveat',
    gridFontSize: fontStyles[0]?.size || 13,

    schoolNameBold: true,

    schoolNameItalic: false,

    dummySchoolNameColor: '#173B67',
    dummySchoolUnderlineColor: '#D4A017',
    uploadedImageScaleWidth: 100,
    uploadedImageScaleHeight: 100,
    uploadedImageOffsetX: 0,
    uploadedImageOffsetY: 0,

    schoolNameColor:
      SCHOOL_NAME_COLORS.find(
        c => c.name === 'Purple'
      )?.value ||
      SCHOOL_NAME_COLORS[0].value,

    schoolBlockOffsetX: 0,
    schoolBlockOffsetY: 0,

    photoOffsetX: 0.0,
    photoOffsetY: 0.0,

    // Global movement for all drawable content inside each label
    contentOffsetX: 0.0,
    contentOffsetY: 0.0,

    // Vertical spacing between label rows in the PDF
    labelVerticalGap: 0.0,

    // Horizontal spacing between the field text and its writing line.
    labelHorizontalGap: 0.0,

    // Horizontal length of label underline/drawing area
    labelLineExtraWidth: 0.0,

    photoWidth: 48,
    photoHeight: 56,

    pdfPhotoWidth: 10,
    pdfPhotoHeight: 10,

    uploadedPhoto: null,
    uploadedPhotoPreview: '',

    schoolNameToAddress1Gap: 3.5,
    address1ToAddress2Gap: 3.5,
  });

  /* ================= HANDLERS ================= */

  const handleGeneratePDF = async () => {

    if (isDummyMode) {
    const requiredFields = [
      ['School Label', labels.school],
      ['Class', labels.classStd],
      ['Name', labels.name],
      ['Study', labels.study],
      ['Roll No', labels.rollNo],
      ['Section', labels.sec],
      ['Semester', labels.semester],
    ];
    const missingFields = requiredFields
      .filter(([, value]) => !String(value ?? '').trim())
      .map(([name]) => name);

    if (missingFields.length) {
      alert(`Please complete these required label fields: ${missingFields.join(', ')}.`);
      return;
    }
    // Plain Mode is text-only and must work without any uploaded images.
    // Animation Mode continues to require both border and artwork images.
    if (imageMode !== 'plain') {
      if (!uploadedBorderImages.length) {
        alert('Please upload at least one border image for Dummy Mode.');
        return;
      }
      if (!uploadedArtworkImages.length) {
        alert('Please upload at least one photo/artwork image for Dummy Mode.');
        return;
      }
    }
    }

    await generatePDF({

      orientation,

      headerMode,

      labels,

      classLabel,

      fontStyles,

      labelColors,

      valueColors,

      lineColors,

      setGridCounts,

      setTotalGrids,

      mode,

      imageMode,
      dummyMode,

      printingMethod,
      uploadedBorderImages,
      uploadedArtworkImages,
    });
  };

  const handlePhotoUpload = (e) => {

    const file = e.target.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {

      setLabels(prev => ({
        ...prev,
        uploadedPhoto: file,
        uploadedPhotoPreview: reader.result,
      }));
    };

    reader.readAsDataURL(file);
  };

  const readImageFiles = (fileList) => Promise.all(
    Array.from(fileList || []).filter(file => file.type.startsWith('image/')).map((file, index) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const image = new Image();
          image.onload = () => {
            // Normalize every supported browser image (including WebP) to PNG for jsPDF.
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth || image.width;
            canvas.height = image.naturalHeight || image.height;
            const context = canvas.getContext('2d');
            if (!context) {
              reject(new Error('Unable to create image canvas.'));
              return;
            }
            context.drawImage(image, 0, 0);
            resolve({
              id: `${file.name}-${file.lastModified}-${file.size}-${Date.now()}-${index}`,
              name: file.name,
              dataUrl: canvas.toDataURL('image/png'),
            });
          };
          image.onerror = () => reject(new Error(`Unable to decode ${file.name}`));
          image.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      })
    )
  );

  const handleBorderImagesUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    try {
      const images = await readImageFiles(files);
      setUploadedBorderImages(prev => [...prev, ...images]);
    } catch (error) {
      console.error('Unable to read border images:', error);
      alert('One or more border images could not be loaded.');
    }
    e.target.value = '';
  };

  const handleArtworkImagesUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    try {
      const images = await readImageFiles(files);
      setUploadedArtworkImages(prev => [...prev, ...images]);
    } catch (error) {
      console.error('Unable to read photo/artwork images:', error);
      alert('One or more photo/artwork images could not be loaded.');
    }
    e.target.value = '';
  };

  /* ================= RENDER ================= */

  return (

    <div
      style={{
        minHeight: '100vh',
        background: '#f1f5f9',
        padding: '24px',
      }}
    >

      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
        }}
      >

        {/* ================= PAGE HEADER ================= */}

        <div
          style={{
            ...cardStyle,
            marginBottom: 20,
            background:
              'linear-gradient(135deg,#2563eb,#7c3aed)',
            color: '#fff',
          }}
        >

          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 800,
            }}
          >
            Notebook Labels PDF Generator
          </h1>

          <p
            style={{
              marginTop: 8,
              opacity: 0.9,
            }}
          >
            Customize school labels with
            premium typography and PDF export.
          </p>

        </div>

        {/* ================= LABEL DRAWING MODE + PRINTING METHOD (2 COLS) ================= */}

        <div
          style={{
            ...cardStyle,
            marginBottom: 20,
          }}
        >

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
              gap: 24,
            }}
          >

            {/* ---- LEFT: Label Drawing Mode ---- */}

            <div style={{ display: dummyMode ? 'none' : 'block' }}>

              <h2
                style={{
                  marginTop: 0,
                  marginBottom: 16,
                  color: '#0f172a',
                  fontSize: 18,
                }}
              >
                Label Drawing Mode
              </h2>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >

                  <input
                    type="radio"
                    name="imageMode"
                    checked={imageMode === 'animation'}
                    onChange={() =>
                      setImageMode('animation')
                    }
                  />

                  Animation Mode

                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >

                  <input
                    type="radio"
                    name="imageMode"
                    checked={imageMode === 'plain'}
                    onChange={() =>
                      setImageMode('plain')
                    }
                  />

                  Plain Mode

                </label>

              </div>

            </div>

            {/* ---- DUMMY MODE ---- */}
            <div>
              <h2 style={{ marginTop: 0, marginBottom: 16, color: '#0f172a', fontSize: 18 }}>Dummy Mode</h2>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, cursor: 'pointer' }}>
                <input type="checkbox" checked={dummyMode} onChange={(e) => setDummyMode(e.target.checked)} />
                Enable Dummy Mode
              </label>
              {dummyMode && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, cursor: 'pointer' }}>
                    <input type="radio" name="dummyImageMode" checked={imageMode === 'animation'} onChange={() => setImageMode('animation')} />
                    Animation Mode
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, cursor: 'pointer' }}>
                    <input type="radio" name="dummyImageMode" checked={imageMode === 'plain'} onChange={() => setImageMode('plain')} />
                    Plain Mode
                  </label>
                </div>
              )}
            </div>

            {/* ---- RIGHT: Printing Method ---- */}

            <div>

              <h2
                style={{
                  marginTop: 0,
                  marginBottom: 16,
                  color: '#0f172a',
                  fontSize: 18,
                }}
              >
                Printing Method
              </h2>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >

                  <input
                    type="radio"
                    name="printingMethod"
                    checked={printingMethod === 'digital'}
                    onChange={() =>
                      setPrintingMethod('digital')
                    }
                  />

                  Digital

                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >

                  <input
                    type="radio"
                    name="printingMethod"
                    checked={printingMethod === 'offset'}
                    onChange={() =>
                      setPrintingMethod('offset')
                    }
                  />

                  Offset

                </label>

              </div>

              {/* Info box */}

              <div
                style={{
                  marginTop: 16,
                  padding: '10px 14px',
                  borderRadius: 10,
                  background:
                    printingMethod === 'offset'
                      ? '#fef3c7'
                      : '#eff6ff',
                  border:
                    `1px solid ${
                      printingMethod === 'offset'
                        ? '#fcd34d'
                        : '#bfdbfe'
                    }`,
                  fontSize: 13,
                  color: '#334155',
                  lineHeight: 1.7,
                }}
              >
                {printingMethod === 'digital' ? (
                  <>
                    <strong>Digital</strong>
                    <br />
                    Sheet: 460 × 305 mm
                    <br />
                    Grid: 5 × 5 — 25 labels
                  </>
                ) : (
                  <>
                    <strong>Offset</strong>
                    <br />
                    Sheet: 635 × 457 mm (H × W)
                    <br />
                    Grid: 5 × 10 — 50 labels
                  </>
                )}
              </div>

            </div>

          </div>

        </div>

        {/* ================= WEB UPLOADS: BORDERS + ARTWORK ================= */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
            gap: 20,
            marginBottom: 24,
          }}
        >
          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: '#0f172a', fontSize: 18 }}>Upload Border Images</h2>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: -6 }}>{dummyMode && imageMode === 'plain' ? 'Optional in Plain Mode: no image upload is required.' : 'Required in Animation Mode: upload at least one border design. Selected borders repeat across labels in the generated PDF.'}</p>
            <input type="file" accept="image/*" multiple onChange={handleBorderImagesUpload} style={inputStyle} />
            <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>{uploadedBorderImages.length} border image(s) selected</div>
            {uploadedBorderImages.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(82px,1fr))', gap: 10, marginTop: 12 }}>
                {uploadedBorderImages.map((img, index) => (
                  <div key={img.id} style={{ position: 'relative', border: '1px solid #e2e8f0', borderRadius: 8, padding: 5 }}>
                    <img src={img.dataUrl} alt={img.name} style={{ width: '100%', height: 58, objectFit: 'cover', borderRadius: 5 }} />
                    <button type="button" title="Remove image" onClick={() => setUploadedBorderImages(prev => prev.filter((_, i) => i !== index))} style={{ position: 'absolute', top: 2, right: 2, border: 0, borderRadius: 20, background: '#dc2626', color: '#fff', width: 22, height: 22, cursor: 'pointer' }}>×</button>
                    <div title={img.name} style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <h2 style={{ marginTop: 0, color: '#0f172a', fontSize: 18 }}>Upload Multiple Photo Images</h2>
            <p style={{ color: '#64748b', fontSize: 13, marginTop: -6 }}>{dummyMode && imageMode === 'plain' ? 'Optional in Plain Mode: no image upload is required.' : 'Required in Animation Mode: upload at least one photo/artwork image. Images repeat in sequence across labels.'}</p>
            <input type="file" accept="image/*" multiple onChange={handleArtworkImagesUpload} style={inputStyle} />
            <div style={{ marginTop: 12, fontSize: 13, color: '#475569' }}>{uploadedArtworkImages.length} photo/artwork image(s) selected</div>
            {uploadedArtworkImages.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(82px,1fr))', gap: 10, marginTop: 12 }}>
                {uploadedArtworkImages.map((img, index) => (
                  <div key={img.id} style={{ position: 'relative', border: '1px solid #e2e8f0', borderRadius: 8, padding: 5 }}>
                    <img src={img.dataUrl} alt={img.name} style={{ width: '100%', height: 58, objectFit: 'cover', borderRadius: 5 }} />
                    <button type="button" title="Remove image" onClick={() => setUploadedArtworkImages(prev => prev.filter((_, i) => i !== index))} style={{ position: 'absolute', top: 2, right: 2, border: 0, borderRadius: 20, background: '#dc2626', color: '#fff', width: 22, height: 22, cursor: 'pointer' }}>×</button>
                    <div title={img.name} style={{ fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ================= LIVE PREVIEW ================= */}

        <div
          style={{
            ...cardStyle,
            marginBottom: 24,
          }}
        >

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 18,
              flexWrap: 'wrap',
              gap: 12,
            }}
          >

            <div>

              <h2
                style={{
                  margin: 0,
                  fontSize: 24,
                  fontWeight: 800,
                  color: '#0f172a',
                }}
              >
                Live Label Preview
              </h2>

              <div
                style={{
                  marginTop: 4,
                  color: '#64748b',
                  fontSize: 14,
                }}
              >
                Real-time preview synced with PDF
              </div>

            </div>

            <div
              style={{
                background: '#eff6ff',
                color: '#2563eb',
                padding: '8px 14px',
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              LIVE
            </div>

          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >

            <div
              style={{
                position: 'relative',

                width: 380,
                height: 240,

                borderRadius: 18,

                overflow: 'hidden',

                backgroundImage:
                  uploadedBorderImages[0]?.dataUrl
                    ? `url(${uploadedBorderImages[0].dataUrl})`
                    : 'none',

                backgroundSize: 'cover',

                backgroundPosition: 'center',

                boxShadow:
                  '0 18px 50px rgba(0,0,0,0.16)',
              }}
            >

              {/* PHOTO */}

              {labels.uploadedPhotoPreview && (

                <img
                  src={labels.uploadedPhotoPreview}

                  alt="preview"

                  style={{
                    position: 'absolute',

                    left:
                      12 +
                      labels.photoOffsetX +
                      (isDummyMode ? 0 : labels.contentOffsetX),

                    top:
                      12 +
                      labels.photoOffsetY +
                      (isDummyMode ? 0 : labels.contentOffsetY),

                    width: labels.photoWidth,

                    height: labels.photoHeight,

                    objectFit: 'cover',

                    borderRadius: 6,

                    border: '2px solid #fff',

                    boxShadow:
                      '0 6px 14px rgba(0,0,0,0.25)',

                    transition: 'all 0.15s ease',
                  }}
                />
              )}

              {/* SCHOOL BLOCK: hidden in Dummy Mode */}

              {!isDummyMode && <div
                style={{
                  position: 'absolute',

                  left:
                    190 +
                    labels.schoolBlockOffsetX +
                    labels.contentOffsetX,

                  top:
                    18 +
                    labels.schoolBlockOffsetY +
                    labels.contentOffsetY,

                  transform: 'translateX(-50%)',

                  textAlign: 'center',

                  width: 280,
                }}
              >

                <div
                  style={{
                    fontSize:
                      labels.schoolNameFontSize,

                    fontWeight:
                      labels.schoolNameBold
                        ? 800
                        : 500,

                    fontStyle:
                      labels.schoolNameItalic
                        ? 'italic'
                        : 'normal',

                    color:
                      labels.schoolNameColor,

                    lineHeight: 1.1,

                    textShadow:
                      '0 1px 2px rgba(0,0,0,0.15)',
                  }}
                >
                  {labels.schoolName ||
                    'Your School Name'}
                </div>

                <div
                  style={{
                    marginTop: 4,

                    fontSize:
                      labels.schoolAddressLine1FontSize,

                    color: '#111827',
                  }}
                >
                  {labels.schoolAddressLine1 ||
                    'Address Line 1'}
                </div>

                <div
                  style={{
                    marginTop: 2,

                    fontSize:
                      labels.schoolAddressLine2FontSize,

                    color: '#111827',
                  }}
                >
                  {labels.schoolAddressLine2 ||
                    'Address Line 2'}
                </div>

              </div>}

            </div>

          </div>

        </div>

        {/* ================= SCHOOL HEADER SETTINGS ================= */}

        {!isDummyMode && <div style={cardStyle}>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20,
            }}
          >

            <input
              type="radio"
              name="headerMode"
              checked={headerMode === 'schoolName'}
              onChange={() =>
                setHeaderMode('schoolName')
              }
            />

            <h2
              style={{
                margin: 0,
                color: '#0f172a',
              }}
            >
              School Header Settings
            </h2>

          </div>

          <div
            style={{
              display: 'grid',
              gap: 18,
            }}
          >

            <FieldRow
              label="School Name"

              sizeValue={labels.schoolNameFontSize}

              onDecrease={() =>
                setLabels({
                  ...labels,
                  schoolNameFontSize:
                    Math.max(
                      9,
                      Number(
                        (labels.schoolNameFontSize - 0.2).toFixed(1)
                      )
                    )
                })
              }

              onIncrease={() =>
                setLabels({
                  ...labels,
                  schoolNameFontSize:
                    Math.min(
                      45,
                      Number(
                        (labels.schoolNameFontSize + 0.2).toFixed(1)
                      )
                    )
                })
              }
            >

              <input
                type="text"

                value={labels.schoolName}

                disabled={headerMode !== 'schoolName'}

                onChange={(e) =>
                  setLabels({
                    ...labels,
                    schoolName: e.target.value
                  })
                }

                style={inputStyle}
              />

            </FieldRow>

            <FieldRow
              label="Address Line 1"

              sizeValue={labels.schoolAddressLine1FontSize}

              onDecrease={() =>
                setLabels({
                  ...labels,
                  schoolAddressLine1FontSize:
                    Math.max(
                      6,
                      labels.schoolAddressLine1FontSize - 0.5
                    )
                })
              }

              onIncrease={() =>
                setLabels({
                  ...labels,
                  schoolAddressLine1FontSize:
                    Math.min(
                      20,
                      labels.schoolAddressLine1FontSize + 0.5
                    )
                })
              }
            >

              <input
                type="text"

                value={labels.schoolAddressLine1}

                disabled={headerMode !== 'schoolName'}

                onChange={(e) =>
                  setLabels({
                    ...labels,
                    schoolAddressLine1: e.target.value
                  })
                }

                style={inputStyle}
              />

            </FieldRow>

            <FieldRow
              label="Address Line 2"

              sizeValue={labels.schoolAddressLine2FontSize}

              onDecrease={() =>
                setLabels({
                  ...labels,
                  schoolAddressLine2FontSize:
                    Math.max(
                      6,
                      labels.schoolAddressLine2FontSize - 1
                    )
                })
              }

              onIncrease={() =>
                setLabels({
                  ...labels,
                  schoolAddressLine2FontSize:
                    Math.min(
                      20,
                      labels.schoolAddressLine2FontSize + 1
                    )
                })
              }
            >

              <input
                type="text"

                value={labels.schoolAddressLine2}

                disabled={headerMode !== 'schoolName'}

                onChange={(e) =>
                  setLabels({
                    ...labels,
                    schoolAddressLine2: e.target.value
                  })
                }

                style={inputStyle}
              />

            </FieldRow>

          </div>

          {/* ================= ADDRESS GAP ================= */}

          <div>

            <h2>
              Vertical Space between School Name and Address lines 1, 2
            </h2>

            <input
              type="number"
              step="0.5"
              min="0.5"
              value={labels.schoolNameToAddress1Gap}
              onChange={(e) =>
                setLabels({
                  ...labels,
                  schoolNameToAddress1Gap:
                    Number(e.target.value)
                })
              }
            />

            <input
              type="number"
              step="0.5"
              min="0.5"
              value={labels.address1ToAddress2Gap}
              onChange={(e) =>
                setLabels({
                  ...labels,
                  address1ToAddress2Gap:
                    Number(e.target.value)
                })
              }
            />

          </div>

          {/* ================= PHOTO UPLOAD ================= */}

          <FieldRow label="Upload Photo">

            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={inputStyle}
            />

          </FieldRow>

          {/* ================= GRID WRITING TYPOGRAPHY ================= */}
          <div style={{ ...cardStyle, marginTop: 24 }}>
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: 16 }}>Grid Writing Font</h3>
            <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 12 }}>Choose one font and size for all text written inside every PDF grid, including Name, Study, Roll No., Subject and School labels.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
              <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>Font Family
                <select
                  value={labels.gridFontStyle}
                  onChange={(e) => {
                    const selected = fontStyles.find((f) => f.font === e.target.value);
                    setLabels((prev) => ({ ...prev, gridFontStyle: e.target.value, gridFontSize: selected?.size ?? prev.gridFontSize }));
                  }}
                  style={inputStyle}
                >
                  {fontStyles.map((f) => <option key={f.font} value={f.font}>{f.font} (default {f.size} pt)</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>Font Size (pt)
                <select
                  value={String(labels.gridFontSize)}
                  onChange={(e) => setLabels((prev) => ({ ...prev, gridFontSize: Number(e.target.value) }))}
                  style={inputStyle}
                >
                  {Array.from({ length: 25 }, (_, i) => 6 + i * 0.5).map((size) => (
                    <option key={size} value={size}>{size.toFixed(1).replace(/\.0$/, '')} pt</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* ================= TYPOGRAPHY ================= */}

          <div
            style={{
              display: 'grid',

              gridTemplateColumns:
                'repeat(auto-fit,minmax(220px,1fr))',

              gap: 18,

              marginTop: 28,
            }}
          >

            <ControlCard title="Font Style">

              <select
                value={labels.schoolNameFontStyle}

                onChange={(e) =>
                  setLabels({
                    ...labels,
                    schoolNameFontStyle: e.target.value
                  })
                }

                style={inputStyle}
              >

                {fontStyles.map((f) => (

                  <option
                    key={f.font}
                    value={f.font}
                  >
                    {f.font}
                  </option>

                ))}

              </select>

            </ControlCard>

            <ControlCard title="Font Color">

              <select
                value={labels.schoolNameColor}

                onChange={(e) =>
                  setLabels({
                    ...labels,
                    schoolNameColor: e.target.value
                  })
                }

                style={inputStyle}
              >

                {SCHOOL_NAME_COLORS.map((c) => (

                  <option
                    key={c.value}
                    value={c.value}
                  >
                    {c.name}
                  </option>

                ))}

              </select>

            </ControlCard>

            <ControlCard title="Text Style">

              <div
                style={{
                  display: 'flex',
                  gap: 18,
                }}
              >

                <label>

                  <input
                    type="checkbox"

                    checked={labels.schoolNameBold}

                    onChange={(e) =>
                      setLabels({
                        ...labels,
                        schoolNameBold: e.target.checked
                      })
                    }
                  />

                  {' '}Bold

                </label>

                <label>

                  <input
                    type="checkbox"

                    checked={labels.schoolNameItalic}

                    onChange={(e) =>
                      setLabels({
                        ...labels,
                        schoolNameItalic: e.target.checked
                      })
                    }
                  />

                  {' '}Italic

                </label>

              </div>

            </ControlCard>

          </div>

          {/* ================= POSITION CONTROLS ================= */}

          <div
            style={{
              display: 'grid',

              gridTemplateColumns:
                'repeat(auto-fit,minmax(320px,1fr))',

              gap: 18,

              marginTop: 24,
            }}
          >

            {/* SCHOOL BLOCK POSITION */}

            <ControlCard title="School Header Position">

              <MoveControls

                onUp={() =>
                  setLabels(prev => ({
                    ...prev,
                    schoolBlockOffsetY:
                      prev.schoolBlockOffsetY - 0.5
                  }))
                }

                onDown={() =>
                  setLabels(prev => ({
                    ...prev,
                    schoolBlockOffsetY:
                      prev.schoolBlockOffsetY + 0.5
                  }))
                }

                onLeft={() =>
                  setLabels(prev => ({
                    ...prev,
                    schoolBlockOffsetX:
                      prev.schoolBlockOffsetX - 0.5
                  }))
                }

                onRight={() =>
                  setLabels(prev => ({
                    ...prev,
                    schoolBlockOffsetX:
                      prev.schoolBlockOffsetX + 0.5
                  }))
                }
              />

            </ControlCard>

            {/* LOGO CONTROLS */}


            {/* GLOBAL PDF CONTENT CONTROLS */}
            <ControlCard title="PDF Content Set — Move & Spacing">

              <div style={{ display: 'grid', gap: 18 }}>

                <div>
                  <div style={{ marginBottom: 10, fontWeight: 700 }}>
                    Move Entire PDF Set
                  </div>
                  <MoveControls
                    onUp={() => setLabels(prev => ({ ...prev, contentOffsetY: prev.contentOffsetY - 0.5 }))}
                    onDown={() => setLabels(prev => ({ ...prev, contentOffsetY: prev.contentOffsetY + 0.5 }))}
                    onLeft={() => setLabels(prev => ({ ...prev, contentOffsetX: prev.contentOffsetX - 0.5 }))}
                    onRight={() => setLabels(prev => ({ ...prev, contentOffsetX: prev.contentOffsetX + 0.5 }))}
                  />
                </div>

                <SizeRow
                  label="Label Vertical Gap"
                  value={labels.labelVerticalGap}
                  onDecrease={() => setLabels(prev => ({
                    ...prev,
                    labelVerticalGap: Math.max(-3, Number((prev.labelVerticalGap - 0.5).toFixed(1)))
                  }))}
                  onIncrease={() => setLabels(prev => ({
                    ...prev,
                    labelVerticalGap: Math.min(10, Number((prev.labelVerticalGap + 0.5).toFixed(1)))
                  }))}
                />

                <SizeRow
                  label="Label Line Width"
                  value={labels.labelLineExtraWidth}
                  onDecrease={() => setLabels(prev => ({
                    ...prev,
                    labelLineExtraWidth: Math.max(-10, Number((prev.labelLineExtraWidth - 1).toFixed(1)))
                  }))}
                  onIncrease={() => setLabels(prev => ({
                    ...prev,
                    labelLineExtraWidth: Math.min(30, Number((prev.labelLineExtraWidth + 1).toFixed(1)))
                  }))}
                />

              </div>
            </ControlCard>

            <ControlCard title="Logo Controls">

              <div
                style={{
                  display: 'grid',
                  gap: 20,
                }}
              >

                <div>

                  <div
                    style={{
                      marginBottom: 10,
                      fontWeight: 700,
                    }}
                  >
                    Logo Position
                  </div>

                  <MoveControls

                    onUp={() =>
                      setLabels(prev => ({
                        ...prev,
                        photoOffsetY:
                          prev.photoOffsetY - 0.2
                      }))
                    }

                    onDown={() =>
                      setLabels(prev => ({
                        ...prev,
                        photoOffsetY:
                          prev.photoOffsetY + 0.2
                      }))
                    }

                    onLeft={() =>
                      setLabels(prev => ({
                        ...prev,
                        photoOffsetX:
                          prev.photoOffsetX - 0.2
                      }))
                    }

                    onRight={() =>
                      setLabels(prev => ({
                        ...prev,
                        photoOffsetX:
                          prev.photoOffsetX + 0.2
                      }))
                    }
                  />

                </div>

                {/* PREVIEW SIZE */}

                <div>

                  <div
                    style={{
                      marginBottom: 10,
                      fontWeight: 700,
                    }}
                  >
                    Preview Logo Size
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gap: 12,
                    }}
                  >

                    <SizeRow
                      label="Width"
                      value={labels.photoWidth}
                      step={1}
                      min={20}
                      onDecrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          photoWidth:
                            Math.max(20, prev.photoWidth - 1)
                        }))
                      }
                      onIncrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          photoWidth: prev.photoWidth + 1
                        }))
                      }
                    />

                    <SizeRow
                      label="Height"
                      value={labels.photoHeight}
                      step={1}
                      min={20}
                      onDecrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          photoHeight:
                            Math.max(20, prev.photoHeight - 1)
                        }))
                      }
                      onIncrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          photoHeight: prev.photoHeight + 1
                        }))
                      }
                    />

                  </div>

                </div>

                {/* PDF SIZE */}

                <div>

                  <div
                    style={{
                      marginBottom: 10,
                      fontWeight: 700,
                    }}
                  >
                    PDF Logo Size
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gap: 12,
                    }}
                  >

                    <SizeRow
                      label="PDF Width"
                      value={labels.pdfPhotoWidth}
                      onDecrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          pdfPhotoWidth:
                            Math.max(4, prev.pdfPhotoWidth - 0.5)
                        }))
                      }
                      onIncrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          pdfPhotoWidth:
                            prev.pdfPhotoWidth + 0.5
                        }))
                      }
                    />

                    <SizeRow
                      label="PDF Height"
                      value={labels.pdfPhotoHeight}
                      onDecrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          pdfPhotoHeight:
                            Math.max(4, prev.pdfPhotoHeight - 0.5)
                        }))
                      }
                      onIncrease={() =>
                        setLabels(prev => ({
                          ...prev,
                          pdfPhotoHeight:
                            prev.pdfPhotoHeight + 0.5
                        }))
                      }
                    />

                  </div>

                </div>

              </div>

            </ControlCard>

          </div>

          {/* ================= HEADER MODES ================= */}

          <div
            style={{
              marginTop: 26,
              display: 'grid',
              gap: 14,
            }}
          >

            <RadioRow
              checked={headerMode === 'school'}
              onChange={() =>
                setHeaderMode('school')
              }
            >
              <strong>Pitics Logo</strong>
            </RadioRow>

            <RadioRow
              checked={headerMode === 'logo'}
              onChange={() =>
                setHeaderMode('logo')
              }
            >
              <strong>School Logo (Border Mode)</strong>
            </RadioRow>

          </div>

        </div>

        }

        {isDummyMode && (
          <div style={{ ...cardStyle, marginTop: 20, display: 'grid', gap: 18 }}>
            <h2 style={{ margin: 0, color: '#0f172a' }}>Dummy Line Adjustments</h2>
            <div style={{ border: '1px solid #bfdbfe', borderRadius: 12, padding: 14, display: 'grid', gap: 10 }}>
              <h3 style={{ margin: 0, color: '#0f172a', fontSize: 16 }}>Move Labels Within Border / Grid</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: 12 }}>
                Move the text and label content inside each fixed border/grid horizontally or vertically. The border and grid positions remain unchanged in the live preview and generated PDF.
              </p>
              <MoveControls
                onUp={() => setLabels(prev => ({ ...prev, contentOffsetY: Number((prev.contentOffsetY - 0.5).toFixed(1)) }))}
                onDown={() => setLabels(prev => ({ ...prev, contentOffsetY: Number((prev.contentOffsetY + 0.5).toFixed(1)) }))}
                onLeft={() => setLabels(prev => ({ ...prev, contentOffsetX: Number((prev.contentOffsetX - 0.5).toFixed(1)) }))}
                onRight={() => setLabels(prev => ({ ...prev, contentOffsetX: Number((prev.contentOffsetX + 0.5).toFixed(1)) }))}
              />
              <div style={{ textAlign: 'center', fontSize: 12, color: '#475569' }}>
                Horizontal: {Number(labels.contentOffsetX || 0).toFixed(1)} mm&nbsp; | &nbsp;Vertical: {Number(labels.contentOffsetY || 0).toFixed(1)} mm
              </div>
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, display: 'grid', gap: 12 }}>
              <h3 style={{ margin: 0, color: '#0f172a', fontSize: 16 }}>Grid Writing Font</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: 12 }}>
                Select the font and size to use for all writings inside every PDF grid, including Name, Study, Roll No., Section, Semester, Subject and School Label.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>
                  Font Family
                  <select
                    value={labels.gridFontStyle}
                    onChange={(e) => {
                      const selected = fontStyles.find((f) => f.font === e.target.value);
                      setLabels((prev) => ({
                        ...prev,
                        gridFontStyle: e.target.value,
                        gridFontSize: selected?.size ?? prev.gridFontSize,
                      }));
                    }}
                    style={inputStyle}
                  >
                    {fontStyles.map((f) => (
                      <option key={f.font} value={f.font}>
                        {f.font} (default {f.size} pt)
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>
                  Font Size (pt)
                  <select
                    value={String(labels.gridFontSize)}
                    onChange={(e) => setLabels((prev) => ({
                      ...prev,
                      gridFontSize: Number(e.target.value),
                    }))}
                    style={inputStyle}
                  >
                    {Array.from({ length: 25 }, (_, i) => 6 + i * 0.5).map((size) => (
                      <option key={size} value={size}>
                        {size.toFixed(1).replace(/\\.0$/, '')} pt
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <SizeRow
              label="Vertical Space Between Lines"
              value={labels.labelVerticalGap}
              onDecrease={() => setLabels(prev => ({ ...prev, labelVerticalGap: Math.max(-3, Number((prev.labelVerticalGap - 0.5).toFixed(1))) }))}
              onIncrease={() => setLabels(prev => ({ ...prev, labelVerticalGap: Math.min(10, Number((prev.labelVerticalGap + 0.5).toFixed(1))) }))}
            />
            <SizeRow
              label="Horizontal Space Before Lines"
              value={labels.labelHorizontalGap}
              onDecrease={() => setLabels(prev => ({ ...prev, labelHorizontalGap: Math.max(-5, Number((prev.labelHorizontalGap - 0.5).toFixed(1))) }))}
              onIncrease={() => setLabels(prev => ({ ...prev, labelHorizontalGap: Math.min(15, Number((prev.labelHorizontalGap + 0.5).toFixed(1))) }))}
            />
            <SizeRow
              label="Line Length"
              value={labels.labelLineExtraWidth}
              onDecrease={() => setLabels(prev => ({ ...prev, labelLineExtraWidth: Math.max(-10, Number((prev.labelLineExtraWidth - 1).toFixed(1))) }))}
              onIncrease={() => setLabels(prev => ({ ...prev, labelLineExtraWidth: Math.min(30, Number((prev.labelLineExtraWidth + 1).toFixed(1))) }))}
            />
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14 }}>
              <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>School Name Color
                <input type="color" value={labels.dummySchoolNameColor} onChange={e => setLabels(prev => ({ ...prev, dummySchoolNameColor: e.target.value }))} style={{ width: '100%', height: 40, padding: 3, border: '1px solid #cbd5e1', borderRadius: 8 }} />
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 13, fontWeight: 600 }}>School Name Underline Color
                <input type="color" value={labels.dummySchoolUnderlineColor} onChange={e => setLabels(prev => ({ ...prev, dummySchoolUnderlineColor: e.target.value }))} style={{ width: '100%', height: 40, padding: 3, border: '1px solid #cbd5e1', borderRadius: 8 }} />
              </label>
            </div>
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, display: 'grid', gap: 12 }}>
              <h3 style={{ margin: 0, color: '#334155', fontSize: 15 }}>Common Uploaded Image Size & Position</h3>
              <p style={{ margin: 0, color: '#64748b', fontSize: 12 }}>These settings apply to every uploaded border and artwork image in Dummy Mode.</p>
              <SizeRow label="Image Width (%)" value={labels.uploadedImageScaleWidth} onDecrease={() => setLabels(prev => ({ ...prev, uploadedImageScaleWidth: Math.max(20, prev.uploadedImageScaleWidth - 5) }))} onIncrease={() => setLabels(prev => ({ ...prev, uploadedImageScaleWidth: Math.min(150, prev.uploadedImageScaleWidth + 5) }))} />
              <SizeRow label="Image Height (%)" value={labels.uploadedImageScaleHeight} onDecrease={() => setLabels(prev => ({ ...prev, uploadedImageScaleHeight: Math.max(20, prev.uploadedImageScaleHeight - 5) }))} onIncrease={() => setLabels(prev => ({ ...prev, uploadedImageScaleHeight: Math.min(150, prev.uploadedImageScaleHeight + 5) }))} />
              <SizeRow label="Move Images Horizontally (mm)" value={labels.uploadedImageOffsetX} onDecrease={() => setLabels(prev => ({ ...prev, uploadedImageOffsetX: Math.max(-30, prev.uploadedImageOffsetX - 1) }))} onIncrease={() => setLabels(prev => ({ ...prev, uploadedImageOffsetX: Math.min(30, prev.uploadedImageOffsetX + 1) }))} />
              <SizeRow label="Move Images Vertically (mm)" value={labels.uploadedImageOffsetY} onDecrease={() => setLabels(prev => ({ ...prev, uploadedImageOffsetY: Math.max(-30, prev.uploadedImageOffsetY - 1) }))} onIncrease={() => setLabels(prev => ({ ...prev, uploadedImageOffsetY: Math.min(30, prev.uploadedImageOffsetY + 1) }))} />
            </div>
          </div>
        )}

        {/* ================= LABEL SETTINGS ================= */}

        <div
          style={{
            ...cardStyle,
            marginTop: 20,
          }}
        >

          <h2
            style={{
              marginTop: 0,
              marginBottom: 18,
              color: '#0f172a',
            }}
          >
            Label Settings
          </h2>

          <div
            style={{
              display: 'grid',

              gridTemplateColumns:
                'repeat(auto-fit,minmax(260px,1fr))',

              gap: 16,
            }}
          >

            <SimpleInput
              title={isDummyMode ? "School Label" : "School Name"}
              value={isDummyMode ? labels.school : labels.schoolName}
              onChange={(v) => setLabels(prev => ({ ...prev, [isDummyMode ? 'school' : 'schoolName']: v }))}
            />

            <LabelSelect
              title="All Classes"

              value={labels.classStd}

              options={[
                'All',
                'Nursery',
                'LKG',
                'UKG',
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
                '10',
              ]}

              onChange={(v) =>
                setLabels({ ...labels, classStd: v })
              }
            />

            <LabelSelect
              title="Name"

              value={labels.name}

              options={['Name', 'Student']}

              onChange={(v) =>
                setLabels({ ...labels, name: v })
              }
            />

            <LabelSelect
              title="Study"

              value={labels.study}

              options={['Study', 'Class']}

              onChange={(v) =>
                setLabels({ ...labels, study: v })
              }
            />

            <LabelSelect
              title="Roll No"

              value={labels.rollNo}

              options={['Roll No', 'Adm No']}

              onChange={(v) =>
                setLabels({ ...labels, rollNo: v })
              }
            />

            <SimpleInput
              title="Section"

              value={labels.sec}

              onChange={(v) =>
                setLabels({ ...labels, sec: v })
              }
            />

            <SimpleInput
              title="Semester"

              value={labels.semester}

              onChange={(v) =>
                setLabels({ ...labels, semester: v })
              }
            />

          </div>

        </div>

        {/* ================= DOWNLOAD ================= */}

        <div
          style={{
            textAlign: 'center',
            marginTop: 28,
          }}
        >

          <button
            onClick={handleGeneratePDF}

            style={{
              background:
                'linear-gradient(135deg,#2563eb,#7c3aed)',

              color: '#fff',

              border: 'none',

              padding: '14px 34px',

              borderRadius: 14,

              fontWeight: 700,

              fontSize: 16,

              cursor: 'pointer',

              boxShadow:
                '0 8px 18px rgba(37,99,235,0.25)',
            }}
          >
            Download PDF
          </button>

        </div>

        {/* ================= FOOTER ================= */}

        <div
          style={{
            textAlign: 'center',
            marginTop: 26,
            color: '#64748b',
            fontSize: 14,
          }}
        >
          © 2025 Tringe.in | Label Generate Tool
        </div>

      </div>

    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function FieldRow({
  label,
  children,
  sizeValue,
  onIncrease,
  onDecrease,
}) {

  return (

    <div
      style={{
        display: 'grid',

        gridTemplateColumns:
          '180px 1fr 130px',

        gap: 14,

        alignItems: 'center',
      }}
    >

      <div
        style={{
          fontWeight: 700,
          color: '#334155',
        }}
      >
        {label}
      </div>

      {children}

      {sizeValue !== undefined && (

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            background: '#f8fafc',
            borderRadius: 10,
            padding: '6px 10px',
            border: '1px solid #e2e8f0',
          }}
        >

          <button onClick={onDecrease}>−</button>

          <strong>{sizeValue}</strong>

          <button onClick={onIncrease}>+</button>

        </div>
      )}

    </div>
  );
}

function SizeRow({
  label,
  value,
  onDecrease,
  onIncrease,
}) {

  return (

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >

      <span>{label}</span>

      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
        }}
      >

        <button onClick={onDecrease}>−</button>

        <strong>{value}</strong>

        <button onClick={onIncrease}>+</button>

      </div>

    </div>
  );
}

function ControlCard({ title, children }) {

  return (

    <div
      style={{
        background: '#f8fafc',
        borderRadius: 14,
        padding: 16,
        border: '1px solid #e2e8f0',
      }}
    >

      <div
        style={{
          marginBottom: 10,
          fontWeight: 700,
          color: '#334155',
        }}
      >
        {title}
      </div>

      {children}

    </div>
  );
}

function RadioRow({ checked, onChange, children }) {

  return (

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >

      <input
        type="radio"
        checked={checked}
        onChange={onChange}
      />

      {children}

    </div>
  );
}

function LabelSelect({
  title,
  value,
  options,
  onChange,
}) {

  return (

    <div>

      <div
        style={{
          marginBottom: 8,
          fontWeight: 700,
          color: '#334155',
        }}
      >
        {title}
      </div>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      >

        {options.map((opt) => (

          <option key={opt} value={opt}>
            {opt}
          </option>

        ))}

      </select>

    </div>
  );
}

function SimpleInput({ title, value, onChange }) {

  return (

    <div>

      <div
        style={{
          marginBottom: 8,
          fontWeight: 700,
          color: '#334155',
        }}
      >
        {title}
      </div>

      <input
        type="text"

        value={value}

        onChange={(e) => onChange(e.target.value)}

        style={inputStyle}
      />

    </div>
  );
}

export default SheetToPDF;
